import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowRight, CheckCircle2, ChevronRight, Inbox, Printer } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { Label } from '@/components/ui/Label'
import { Badge } from '@/components/ui/Badge'
import { formatDateTime } from '@/lib/format'
import { CountsCard } from './components/CountsCard'
import { useAcknowledgeHandover, useHandover } from './hooks'
import type { HandoverDetail } from './types'

/**
 * AF-135 — incoming-shift acknowledgement view.
 *
 * Renders the full handover (notes + pending actions + escalations +
 * attachments + audit) for a submitted handover, plus an Acknowledge
 * button and an optional comment. Acknowledging records the time and
 * notifies the outgoing analyst.
 *
 * Print button uses `window.print()` so the browser handles PDF export
 * via "Save as PDF". The print stylesheet hides chrome (sidebar/header)
 * via `@media print` rules in index.css.
 */
export function HandoverAcknowledgePage() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading, isError } = useHandover(id)

  if (isLoading) return <Skeleton className="h-96 w-full" />
  if (isError || !data) {
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-muted-fg">Couldn't load handover {id}.</p>
        </CardContent>
      </Card>
    )
  }
  return <View detail={data} />
}

function View({ detail }: { detail: HandoverDetail }) {
  const ack = useAcknowledgeHandover()
  const [comment, setComment] = useState('')

  const onAck = () => {
    ack.mutate({ id: detail.id, comment: comment.trim() || undefined })
  }

  return (
    <div className="flex flex-col gap-4 print:bg-card">
      <Breadcrumbs />
      <header className="flex flex-wrap items-end justify-between gap-3 print:hidden">
        <div>
          <h1 className="text-xl font-semibold">
            Handover from {detail.outgoing.display_name}
          </h1>
          <p className="text-sm text-muted-fg">
            {detail.submitted_at &&
              `Submitted ${formatDateTime(detail.submitted_at)}`}{' '}
            · Shift <span className="font-mono">{detail.shift_id}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={statusVariant(detail.status)} className="capitalize">
            {detail.status}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
            aria-label="Print or save as PDF"
          >
            <Printer size={14} aria-hidden /> Print / PDF
          </Button>
        </div>
      </header>

      <CountsCard counts={detail.counts} />

      <Card>
        <CardHeader>
          <CardTitle>Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="whitespace-pre-wrap text-sm">{detail.handover_notes}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pending actions</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="whitespace-pre-wrap text-sm">{detail.pending_actions}</p>
        </CardContent>
      </Card>

      {detail.escalations && detail.escalations.trim() && (
        <Card>
          <CardHeader>
            <CardTitle>Escalations</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm">{detail.escalations}</p>
          </CardContent>
        </Card>
      )}

      {detail.attachments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Attached items ({detail.attachments.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-wrap gap-1.5">
              {detail.attachments.map((a) => (
                <li key={a.id}>
                  <Link
                    to={hrefFor(a)}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 py-1 text-xs hover:bg-muted"
                  >
                    <span className="text-muted-fg uppercase">{a.type}</span>
                    <span className="font-mono">{a.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {detail.status === 'submitted' && (
        <Card className="print:hidden">
          <CardHeader>
            <CardTitle>Acknowledge</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <p className="text-sm text-muted-fg">
              Confirm you've read the handover. Optional comment is saved
              with the audit entry.
            </p>
            <Label htmlFor="ack-comment">Comment (optional)</Label>
            <textarea
              id="ack-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Anything you want the outgoing analyst to know?"
              className="min-h-[80px] rounded border border-input bg-bg p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <div className="flex justify-end">
              <Button onClick={onAck} disabled={ack.isPending}>
                {ack.isPending ? 'Acknowledging…' : 'Acknowledge handover'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {detail.status === 'acknowledged' && detail.acknowledged_at && (
        <PostAckCTA detail={detail} />
      )}

      <AuditCard detail={detail} />
    </div>
  )
}

/**
 * After ack, the analyst's next step is to start working the items
 * the outgoing shift flagged. Surface each attachment as a primary
 * navigation target so they don't have to scroll back up to the
 * Attached items list (which is itself a flat row of chips, not
 * obviously the "what to do next" path).
 *
 * Layout:
 *   - Confirmation chip (timestamp + ack-minutes if available).
 *   - "Pick up where the last shift left off" header + per-attachment
 *     row with type, label, and an Open button that navigates to
 *     the underlying alert/case/block detail page.
 *   - "Go to inbox" fallback when there are no attachments — the
 *     inbox is still a sensible landing for any analyst.
 */
function PostAckCTA({ detail }: { detail: HandoverDetail }) {
  return (
    <Card className="border-status-success/30 bg-status-success/5 print:hidden">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-status-success">
          <CheckCircle2 size={18} aria-hidden /> Acknowledged
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-muted-fg">
          {`Acknowledged ${formatDateTime(detail.acknowledged_at)}`}
          {detail.ack_minutes !== null
            ? ` · ${detail.ack_minutes} min after submission`
            : ''}
        </p>
        {detail.attachments.length > 0 ? (
          <>
            <p className="text-sm font-medium">
              Pick up where the last shift left off:
            </p>
            <ul className="flex flex-col gap-1.5">
              {detail.attachments.map((a) => (
                <li key={`${a.type}-${a.id}`}>
                  <Link
                    to={hrefFor(a)}
                    className="flex items-center justify-between rounded border border-border bg-card px-3 py-2 text-sm hover:border-primary hover:bg-muted"
                  >
                    <span className="flex items-center gap-2">
                      <Badge variant="outline" className="uppercase">
                        {a.type}
                      </Badge>
                      <span className="font-mono">{a.label}</span>
                    </span>
                    <span className="flex items-center gap-1 text-xs text-muted-fg">
                      Open <ArrowRight size={12} aria-hidden />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <Button asChild variant="outline" className="w-fit">
            <Link to="/inbox">
              <Inbox size={14} aria-hidden /> Go to inbox
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

function AuditCard({ detail }: { detail: HandoverDetail }) {
  return (
    <Card className="print:hidden">
      <CardHeader>
        <CardTitle>Audit</CardTitle>
      </CardHeader>
      <CardContent>
        {detail.audit.length === 0 ? (
          <p className="text-sm text-muted-fg">No audit entries.</p>
        ) : (
          <ol className="flex flex-col gap-2">
            {detail.audit.map((a) => (
              <li key={a.id} className="flex flex-col text-sm">
                <span>
                  <span className="font-medium capitalize">
                    {a.action.replace('_', ' ')}
                  </span>{' '}
                  by {a.actor.display_name}
                </span>
                {a.note && <span className="text-xs italic text-muted-fg">{a.note}</span>}
                <span className="text-xs text-muted-fg">{formatDateTime(a.at)}</span>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  )
}

function Breadcrumbs() {
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1 text-sm text-muted-fg print:hidden"
    >
      <Link to="/handovers" className="rounded px-1 py-0.5 hover:bg-muted hover:text-fg">
        Handovers
      </Link>
      <ChevronRight size={12} aria-hidden />
      <span aria-current="page" className="text-fg">
        Inbound
      </span>
    </nav>
  )
}

function statusVariant(s: HandoverDetail['status']) {
  if (s === 'submitted') return 'pending' as const
  if (s === 'acknowledged') return 'success' as const
  return 'default' as const
}

function hrefFor(a: HandoverDetail['attachments'][number]) {
  if (a.type === 'alert') return `/alerts/${a.id}`
  if (a.type === 'case') return `/cases/${a.id}`
  return `/blocks/${a.id}`
}
